import React from 'react';
import { motion } from 'framer-motion';

/**
 * Terms & Conditions page component
 * 
 * @returns React component for the terms and conditions page
 */
const TermsAndConditions = () => {
  return (
    <>
      <section className="pt-36 pb-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div 
            className="max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-3xl md:text-4xl font-bold mb-6">Terms & Conditions</h1>
            <div className="h-1 w-20 bg-gold mb-8"></div>
            <p className="text-charcoal-light mb-4">
              Last Updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <motion.div 
            className="max-w-4xl mx-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.3 }}
          >
            <h2 className="text-2xl font-bold mb-4">1. Acceptance of Terms</h2>
            <p className="mb-6">
              By accessing or using the OptiGold PMO Consulting website ("Website"), you agree to be bound by these Terms and Conditions. If you do not agree to these Terms and Conditions, please do not use the Website.
            </p>

            <h2 className="text-2xl font-bold mb-4 mt-8">2. Changes to Terms</h2>
            <p className="mb-6">
              OptiGold PMO Consulting reserves the right to modify or revise these Terms and Conditions at any time. Your continued use of the Website following the posting of any changes constitutes acceptance of those changes.
            </p>

            <h2 className="text-2xl font-bold mb-4 mt-8">3. Website Use</h2>
            <p className="mb-6">
              You agree to use the Website only for lawful purposes and in a way that does not infringe upon the rights of others or restrict or inhibit anyone else's use of the Website. You agree not to attempt to circumvent any security features of the Website, or to interfere with the proper working of the Website.
            </p>

            <h2 className="text-2xl font-bold mb-4 mt-8">4. Intellectual Property</h2>
            <p className="mb-6">
              All content on this Website, including but not limited to text, graphics, logos, images, audio clips, digital downloads, and software, is the property of OptiGold PMO Consulting or its content suppliers and is protected by international copyright laws. The compilation of all content on this Website is the exclusive property of OptiGold PMO Consulting and is protected by international copyright laws.
            </p>

            <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-gold my-8">
              <h3 className="text-xl font-bold mb-2">Recommendations and Testimonials</h3>
              <p>
                "For recommendations and testimonials the views expressed are those of the individual and do not represent the views of their employer."
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-gold my-8">
              <h3 className="text-xl font-bold mb-2">Use of Interactive Tools</h3>
              <p>
                OptiGold PMO Consulting may provide interactive tools, including but not limited to the PMO Maturity Assessment Tool. These tools are offered for informational purposes only and are not intended to provide professional advice, certifications, or comprehensive evaluations. The results from such tools should not be relied upon for making business, operational, or strategic decisions without further analysis or consultation. OptiGold PMO Consulting disclaims all liability arising from the use of these tools and recommends that users seek professional evaluation for in-depth PMO maturity assessments.
              </p>
            </div>

            <h2 className="text-2xl font-bold mb-4 mt-8">5. Disclaimer of Warranties</h2>
            <p className="mb-6">
              The Website and all information, content, materials, and services included on or otherwise made available to you through the Website are provided on an "as is" and "as available" basis, unless otherwise specified in writing. OptiGold PMO Consulting makes no representations or warranties of any kind, express or implied, as to the operation of the Website or the information, content, materials, or services included on or otherwise made available to you through the Website.
            </p>
            <p className="mb-6">
              You expressly agree that your use of the Website is at your sole risk. To the full extent permissible by applicable law, OptiGold PMO Consulting disclaims all warranties, express or implied, including, but not limited to, implied warranties of merchantability and fitness for a particular purpose.
            </p>

            <h2 className="text-2xl font-bold mb-4 mt-8">6. Limitation of Liability</h2>
            <p className="mb-6">
              OptiGold PMO Consulting will not be liable for any damages of any kind arising from the use of this Website, including, but not limited to direct, indirect, incidental, punitive, and consequential damages, unless otherwise specified in writing.
            </p>

            <h2 className="text-2xl font-bold mb-4 mt-8">7. Third-Party Links</h2>
            <p className="mb-6">
              The Website may contain links to third-party websites or services that are not owned or controlled by OptiGold PMO Consulting. OptiGold PMO Consulting has no control over, and assumes no responsibility for, the content, privacy policies, or practices of any third-party websites or services. You further acknowledge and agree that OptiGold PMO Consulting shall not be responsible or liable, directly or indirectly, for any damage or loss caused or alleged to be caused by or in connection with the use of or reliance on any such content, goods, or services available on or through any such websites or services.
            </p>

            <h2 className="text-2xl font-bold mb-4 mt-8">8. Indemnification</h2>
            <p className="mb-6">
              You agree to indemnify, defend, and hold harmless OptiGold PMO Consulting, its officers, directors, employees, agents, and affiliates, from and against any and all claims, damages, obligations, losses, liabilities, costs, or debt, and expenses (including but not limited to attorney's fees) arising from: (i) your use of and access to the Website; (ii) your violation of any term of these Terms and Conditions; or (iii) your violation of any third-party right, including without limitation any copyright, property, or privacy right.
            </p>

            <h2 className="text-2xl font-bold mb-4 mt-8">9. Governing Law</h2>
            <p className="mb-6">
              These Terms and Conditions shall be governed by and construed in accordance with the laws of the jurisdiction in which OptiGold PMO Consulting is established, without regard to its conflict of law provisions.
            </p>

            <h2 className="text-2xl font-bold mb-4 mt-8">10. Contact Information</h2>
            <p className="mb-2">
              If you have any questions about these Terms and Conditions, please contact us at:
            </p>
            <p className="ml-6 mb-6">
              <strong>Email:</strong> inquiries@optigold.com<br />
              <strong>Website:</strong> www.optigoldpmo.com
            </p>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default TermsAndConditions;
